/**
 * Reading what Payabli's embedded component reports after it photographs a
 * check (remote deposit capture), and pricing the deposit from it. The
 * component calls `functionCallBackReady` with its CheckCapture result once
 * both sides are read.
 *
 * Field names follow Payabli's CheckCapture docs and the sandbox runs
 * (`ocrRoutingNumber`, `ocrAccountNumber`, `ocrCheckNumber`, `ocrAmount`, `id`,
 * `ocrMicrConfidence`, `ocrAmountConfidence`, `amountDiscrepancyDetected`,
 * `endorsementDetected`, `checkType`, and `inputAmount` — what staff typed into
 * the component's CHECK AMOUNT, which Payabli refuses unless it matches the
 * check). Where the record sits in the callback payload was not pinned, so it
 * is searched for rather than assumed, and a payload without it (a per-field
 * validation ping) reads as "no scan yet". The payload also carries the check
 * images; nothing here keeps, logs or forwards them.
 */
import {
  computePaymentMethodDiscount,
  type PaymentMethodDiscountSettings,
} from './paymentMethodDiscount';

/** Below this, a read is flagged for the operator to check against the paper. */
export const LOW_CONFIDENCE_THRESHOLD = 0.8;

/** Payabli's `checkType` for a personal check: 1 personal, 2 business, 0
 *  when the reader could not classify it. */
export const PERSONAL_CHECK_TYPE = 1;

export interface CheckScanRead {
  captureId: string;
  routingNumber: string;
  /**
   * The FULL account number off the MICR line. It goes to the claim once — the
   * server fingerprints it and keeps the last 4 — and is never rendered,
   * logged, or stored anywhere in the browser beyond that request.
   */
  accountNumber: string;
  accountLast4: string;
  checkNumber: string;
  /**
   * The check's face amount, in dollars: what the check is debited for. The
   * amount staff typed and Payabli validated against the paper
   * (`inputAmount`), else what the reader made of it (`ocrAmount`).
   */
  faceAmount: number;
  /** What the reader made of the amount, as reported (the claim's audit copy). */
  ocrAmount: number | null;
  /** What staff typed into the component, as reported. */
  enteredAmount: number | null;
  /** 0–1. */
  amountConfidence: number | null;
  /** 0–1. */
  micrConfidence: number | null;
  amountDiscrepancyDetected: boolean;
  endorsementDetected: boolean;
  checkType: number | null;
}

export type CheckScanParse =
  | { kind: 'read'; read: CheckScanRead }
  /** A check result that is missing something the deposit needs. */
  | { kind: 'incomplete'; missing: string[] }
  /** Not a check result at all. */
  | { kind: 'none' };

const OCR_KEYS = ['ocrAccountNumber', 'ocrRoutingNumber', 'ocrCheckNumber'];

function findOcrRecord(value: unknown, depth = 0): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || depth > 4) return null;
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = findOcrRecord(item, depth + 1);
      if (found) return found;
    }
    return null;
  }
  const record = value as Record<string, unknown>;
  if (OCR_KEYS.some((key) => key in record)) return record;
  for (const nested of Object.values(record)) {
    const found = findOcrRecord(nested, depth + 1);
    if (found) return found;
  }
  return null;
}

/** The first value under `key` anywhere in the payload (it may sit beside the
 *  OCR record rather than inside it). */
function findValue(value: unknown, key: string, depth = 0): unknown {
  if (!value || typeof value !== 'object' || depth > 4) return undefined;
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = findValue(item, key, depth + 1);
      if (found !== undefined) return found;
    }
    return undefined;
  }
  const record = value as Record<string, unknown>;
  if (record[key] !== undefined && record[key] !== null && record[key] !== '') return record[key];
  for (const nested of Object.values(record)) {
    const found = findValue(nested, key, depth + 1);
    if (found !== undefined) return found;
  }
  return undefined;
}

function text(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return '';
}

function numberOrNull(value: unknown): number | null {
  const n =
    typeof value === 'string' && value.trim() ? Number(value.replace(/[$,\s]/g, '')) : value;
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
}

/** Dollars, to the cent; null for anything that is not a positive amount. */
function amountOrNull(value: unknown): number | null {
  const n = numberOrNull(value);
  return n !== null && n > 0 ? Math.round(n * 100) / 100 : null;
}

/**
 * A confidence as a 0–1 fraction. Payabli's docs describe 0–100; the sandbox
 * reported 731 and 999 (a 0–1000 scale). A value above 100 is read as
 * thousandths, one above 1 as a percentage, anything else as a fraction.
 * Ambiguous only for a thousandths value of 100 or less — a read under 10%
 * confident, which the reader rejects outright rather than report.
 */
export function normaliseConfidence(raw: unknown): number | null {
  const n = numberOrNull(raw);
  if (n === null || n < 0) return null;
  if (n <= 1) return n;
  if (n <= 100) return n / 100;
  if (n <= 1000) return n / 1000;
  return null;
}

export function parseCheckScan(payload: unknown): CheckScanParse {
  const record = findOcrRecord(payload);
  if (!record) return { kind: 'none' };
  const captureId = text(record.id) || text(record.checkUniqueId) || text(record.rdcCaptureID);
  const routingNumber = text(record.ocrRoutingNumber).replace(/\D/g, '');
  const accountNumber = text(record.ocrAccountNumber).replace(/[^\d-]/g, '');
  const accountDigits = accountNumber.replace(/\D/g, '');
  const checkNumber = text(record.ocrCheckNumber).replace(/\D/g, '');
  const ocrAmount = amountOrNull(record.ocrAmount);
  // Assumed dollars, as typed ("100.00") — the component sends Payabli cents
  // (`checkAmount`) but reports back what the field held.
  const enteredAmount = amountOrNull(record.inputAmount ?? findValue(payload, 'inputAmount'));
  const faceAmount = enteredAmount ?? ocrAmount;
  const missing = [
    !captureId && 'capture id',
    routingNumber.length !== 9 && 'routing number',
    accountDigits.length < 4 && 'account number',
    !checkNumber && 'check number',
    faceAmount === null && 'amount',
  ].filter((field): field is string => typeof field === 'string');
  if (missing.length > 0 || faceAmount === null) return { kind: 'incomplete', missing };
  const checkType = numberOrNull(record.checkType);
  return {
    kind: 'read',
    read: {
      captureId,
      routingNumber,
      accountNumber,
      accountLast4: accountDigits.slice(-4),
      checkNumber,
      faceAmount,
      ocrAmount,
      enteredAmount,
      amountConfidence: normaliseConfidence(record.ocrAmountConfidence ?? record.amountConfidence),
      micrConfidence: normaliseConfidence(record.ocrMicrConfidence ?? record.micrConfidence),
      amountDiscrepancyDetected: record.amountDiscrepancyDetected === true,
      endorsementDetected: record.endorsementDetected === true,
      checkType: checkType !== null && Number.isInteger(checkType) ? checkType : null,
    },
  };
}

/** A check Payabli classified as anything but personal (0 is "could not tell"). */
export function isBusinessCheck(read: Pick<CheckScanRead, 'checkType'>): boolean {
  return read.checkType !== null && read.checkType !== 0 && read.checkType !== PERSONAL_CHECK_TYPE;
}

/** Why the operator should compare the reader's work against the paper. */
export function checkScanWarnings(read: Omit<CheckScanRead, 'accountNumber'>): string[] {
  const warnings: string[] = [];
  if (
    read.ocrAmount !== null &&
    read.enteredAmount !== null &&
    Math.round(read.ocrAmount * 100) !== Math.round(read.enteredAmount * 100)
  ) {
    warnings.push(
      `The reader made the check out to ${formatUsd(read.ocrAmount)}, but ${formatUsd(read.enteredAmount)} was entered.`,
    );
  }
  if (read.amountDiscrepancyDetected) {
    warnings.push("The written amount and the number on the check don't agree.");
  }
  if (read.amountConfidence === null || read.amountConfidence < LOW_CONFIDENCE_THRESHOLD) {
    warnings.push('The reader is not confident about the amount.');
  }
  if (read.micrConfidence === null || read.micrConfidence < LOW_CONFIDENCE_THRESHOLD) {
    warnings.push('The reader is not confident about the bank numbers.');
  }
  return warnings;
}

// ── Pricing ────────────────────────────────────────────────────────────────

/**
 * How the check's face amount lands on the invoice.
 *
 *   full               — it pays what is due (after the discount, when on)
 *   partial            — it pays less; the rest stays open
 *   over_discounted_due— more than the discounted amount due but not more than
 *                        the balance: paid at full price, no discount
 *   over_balance       — more than the invoice owes; a scanned check cannot
 *                        overpay (the claim refuses it), so it is recorded by
 *                        hand instead
 *   unknown_balance    — the viewer may not see the balance: no discount, and
 *                        the server holds the line on the amount
 */
export type CheckScanPricingKind =
  | 'full'
  | 'partial'
  | 'over_discounted_due'
  | 'over_balance'
  | 'unknown_balance';

export interface CheckScanPricing {
  kind: CheckScanPricingKind;
  /** What the check is debited for: its face amount, always. */
  chargeAmount: number;
  /** The invoice amount the ACH/check discount was priced from; null when none. */
  grossAmount: number | null;
  /** The discount the invoice is credited on top of the check; 0 when none. */
  discountAmount: number;
  /** What the invoice asks of a check (after the discount, when it applies). */
  amountDue: number | null;
  /** What stays open on the invoice after this check. */
  remainingBalance: number | null;
}

/**
 * The largest invoice amount (≤ `maxGrossCents`) whose ACH/check price is
 * exactly `chargeCents` — the manual Check path read backwards: there staff
 * type the amount before discount and record the discounted amount received;
 * here the check fixes the amount received. Null when no such amount earns a
 * discount. The discounted price rises by 0 or 1 cent per cent of gross, so
 * every price up to the discounted balance has a gross, found near
 * `charge / (1 - rate)`.
 */
function grossForCharge(
  settings: PaymentMethodDiscountSettings | null | undefined,
  chargeCents: number,
  maxGrossCents: number,
): number | null {
  const atMax = computePaymentMethodDiscount(settings, maxGrossCents, 'CHECK');
  if (!atMax.enabled) return null;
  const keep = 1 - atMax.percent / 100;
  const estimate = Math.floor(chargeCents / keep);
  // Rounding moves the price half a cent either way, which is up to
  // 1 / keep cents of gross.
  const span = Math.ceil(1 / keep) + 2;
  for (
    let g = Math.min(maxGrossCents, estimate + span);
    g >= Math.max(0, estimate - span);
    g -= 1
  ) {
    const quote = computePaymentMethodDiscount(settings, g, 'CHECK');
    if (quote.chargeCents === chargeCents) return quote.discountCents > 0 ? g : null;
  }
  return null;
}

export function priceScannedCheck(
  faceAmount: number,
  balanceDue: number | null,
  settings: PaymentMethodDiscountSettings | null | undefined,
): CheckScanPricing {
  const face = Math.round(faceAmount * 100);
  const toDollars = (cents: number) => cents / 100;
  const plain = (kind: CheckScanPricingKind, dueCents: number | null): CheckScanPricing => ({
    kind,
    chargeAmount: toDollars(face),
    grossAmount: null,
    discountAmount: 0,
    amountDue: dueCents === null ? null : toDollars(dueCents),
    remainingBalance:
      dueCents === null || balanceDue === null
        ? null
        : toDollars(Math.max(0, Math.round(balanceDue * 100) - face)),
  });
  if (balanceDue === null) return plain('unknown_balance', null);
  const balance = Math.round(balanceDue * 100);
  if (face > balance) return plain('over_balance', balance);
  const quote = computePaymentMethodDiscount(settings, balance, 'CHECK');
  if (!quote.enabled || quote.discountCents <= 0) {
    return plain(face === balance ? 'full' : 'partial', balance);
  }
  if (face > quote.chargeCents) return plain('over_discounted_due', quote.chargeCents);
  const gross = face === quote.chargeCents ? balance : grossForCharge(settings, face, balance);
  if (gross === null) return plain('partial', quote.chargeCents);
  return {
    kind: face === quote.chargeCents ? 'full' : 'partial',
    chargeAmount: toDollars(face),
    grossAmount: toDollars(gross),
    discountAmount: toDollars(gross - face),
    amountDue: toDollars(quote.chargeCents),
    remainingBalance: toDollars(balance - gross),
  };
}

/**
 * What the operator is told about the amount before depositing. `remainder`
 * says where a short check leaves the rest — the invoice by default.
 */
export function checkScanAmountWarnings(
  pricing: CheckScanPricing,
  remainder = 'will stay open on the invoice',
): string[] {
  const face = formatUsd(pricing.chargeAmount);
  switch (pricing.kind) {
    case 'partial':
      return [
        `The check is for ${face}, less than the ${formatUsd(pricing.amountDue ?? 0)} due. ${formatUsd(
          pricing.remainingBalance ?? 0,
        )} ${remainder}.`,
      ];
    case 'over_discounted_due':
      return [
        `The check is for ${face}, more than the ${formatUsd(
          pricing.amountDue ?? 0,
        )} due with the ACH/check discount. It will be recorded at full price, without the discount.`,
      ];
    default:
      return [];
  }
}

function formatUsd(n: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}
